import { useQuery } from '@tanstack/react-query';
import { FC, useMemo } from 'react';
import { Control, UseFieldArrayAppend, useWatch } from 'react-hook-form';
import styled from 'styled-components';

import PulsingLoader, {
  LoaderHolder,
  LoaderTextHolder,
  LoaderTitle,
} from '@/components/PulsingLoader';
import Text from '@/components/Text';
import { api } from '@/utils/api';
import { VideoCompilerCompilationType } from '@/utils/enums';

import { FormDataT, Part } from '..';
import Card from './Card';
import FileCard from './FileCard';

const Center = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 6px;
  flex: 1;
`;

const StyledLoaderHolder = styled(LoaderHolder)`
  box-shadow: none;
  margin: 0;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

interface Props {
  search: string;
  append: UseFieldArrayAppend<FormDataT, 'parts'>;
  control: Control<FormDataT>;
  type: VideoCompilerCompilationType;
  compilationType: string;
}

const SearchCard: FC<Props> = ({
  search,
  append,
  control,
  type,
  compilationType,
}) => {
  const parts = useWatch({ control, name: 'parts' });

  const isQueryEnabled = !!search && !!compilationType && !!type;

  const { data, isLoading, isError } = useQuery({
    queryKey: [
      'video_compiler_search',
      {
        search,
        type,
        compilationType,
      },
    ],
    queryFn: () =>
      api.get<never, { googleDriveFiles: Part[]; localFiles: Part[] }>(
        '/v1/video-compiler/search',
        {
          params: {
            project_id: search,
            type,
            compilation_type: compilationType,
          },
        },
      ),
    enabled: isQueryEnabled,
    staleTime: 1000 * 60 * 5,
  });

  const showEmptyPlaceholder =
    !(isQueryEnabled && isLoading) &&
    !isError &&
    !data?.googleDriveFiles?.length &&
    !data?.localFiles?.length;

  const files = useMemo(() => {
    if (!data) {
      return null;
    }

    if (!parts.length) {
      return [
        ...data.localFiles.filter(
          (file) => !parts.find((part) => part.partId === file.partId),
        ),
        ...data.googleDriveFiles.filter(
          (file) => !parts.find((part) => part.partId === file.partId),
        ),
      ];
    }

    if (compilationType === 'version') {
      return data.googleDriveFiles.filter(
        (file) => !parts.find((part) => part.partId === file.partId),
      );
    }

    return [
      ...data.localFiles.filter(
        (file) =>
          !parts.find(
            (part) => file.type === 'version' && part.partId === file.partId,
          ),
      ),
      ...data.googleDriveFiles.filter(
        (file) =>
          !parts.find(
            (part) => file.type === 'version' && part.partId === file.partId,
          ),
      ),
    ];
  }, [parts, data, compilationType]);

  const hasResults = !!files?.length;

  return (
    <Card
      header={
        data ? (
          <Text $type="smallBold" as="span">
            Search results of:{' '}
            <Text $type="smallBold" $color="gray" as="span">
              {search}
            </Text>
          </Text>
        ) : null
      }
    >
      {isQueryEnabled && isLoading && (
        <Center>
          <StyledLoaderHolder>
            <PulsingLoader />
            <LoaderTextHolder>
              <LoaderTitle $type="button">
                Searching Google Drive and Backbone...
              </LoaderTitle>
              <Text $color="gray" $type="captionSemibold">
                Please be patient, this action may take
                <br />a little longer in some cases
              </Text>
            </LoaderTextHolder>
          </StyledLoaderHolder>
        </Center>
      )}
      {isError && (
        <Center>
          <Text $type="button">Error occurred! Please try again</Text>
        </Center>
      )}
      {showEmptyPlaceholder && (
        <Center>
          <Text $type="button">No results to show</Text>
          <Text $type="smallBold" $color="gray">
            Search files by ID and select them to make changes
          </Text>
        </Center>
      )}
      {hasResults && (
        <List>
          {files?.map((file) => (
            <FileCard
              key={file.partId}
              part={file}
              append={append}
              compilationType={compilationType}
            />
          ))}
        </List>
      )}
    </Card>
  );
};

export default SearchCard;
